// src/routes/AppRoutes.jsx — All routes, role-guarded, lazy loaded
import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import { PageLoader } from '../components/common';
import ErrorBoundary from '../components/ErrorBoundary';

// ── Lazy imports ────────────────────────────────────────────────────────
const Login = lazy(() => import('../pages/Login'));

// Super Admin
const SADashboard     = lazy(() => import('../pages/superadmin/Dashboard'));
const SAStudents      = lazy(() => import('../pages/superadmin/Students'));
const SASchools       = lazy(() => import('../pages/superadmin/Schools'));
const SADoubts        = lazy(() => import('../pages/superadmin/Doubts'));
const SAContent       = lazy(() => import('../pages/superadmin/Content'));
const SASubscriptions = lazy(() => import('../pages/superadmin/Subscriptions'));
const SAPlans         = lazy(() => import('../pages/superadmin/Plans'));
const SAReports       = lazy(() => import('../pages/superadmin/Reports'));
const SASettings      = lazy(() => import('../pages/superadmin/Settings'));
const SACareer        = lazy(() => import('../pages/superadmin/CareerManagement'));
const SAPhase4        = lazy(() => import('../pages/superadmin/Phase4Management'));

// School Admin
const SchoolDashboard   = lazy(() => import('../pages/school/Dashboard'));
const SchoolStudents    = lazy(() => import('../pages/school/Students'));
const SchoolTeachers    = lazy(() => import('../pages/school/Teachers'));
const SchoolClasses     = lazy(() => import('../pages/school/LiveClasses'));
const SchoolAssignments = lazy(() => import('../pages/school/Assignments'));
const SchoolPerformance = lazy(() => import('../pages/school/Performance'));
const SchoolReports     = lazy(() => import('../pages/school/Reports'));
const SchoolDoubts      = lazy(() => import('../pages/school/DoubtAnalytics'));
const SchoolLicense     = lazy(() => import('../pages/school/License'));
const SchoolSettings    = lazy(() => import('../pages/school/Settings'));

// Teacher
const TeacherDashboard     = lazy(() => import('../pages/teacher/Dashboard'));
const TeacherStudents      = lazy(() => import('../pages/teacher/Students'));
const TeacherDoubts        = lazy(() => import('../pages/teacher/Doubts'));
const TeacherLiveClass     = lazy(() => import('../pages/teacher/LiveClass'));
const TeacherSchedule      = lazy(() => import('../pages/teacher/Schedule'));
const TeacherRecordings    = lazy(() => import('../pages/teacher/Recordings'));
const TeacherAssignments   = lazy(() => import('../pages/teacher/Assignments'));
const TeacherAnswerChecker = lazy(() => import('../pages/teacher/AnswerChecker'));
const TeacherPerformance   = lazy(() => import('../pages/teacher/Performance'));
const TeacherReports       = lazy(() => import('../pages/teacher/Reports'));

// ── Route Guard ──────────────────────────────────────────────────────────
const RequireAuth = ({ children, roles }) => {
  const { user, loading, role } = useAuth();
  const location = useLocation();

  if (loading) return <PageLoader />;
  if (!user)   return <Navigate to="/login" state={{ from: location }} replace />;
  if (roles && !roles.includes(role)) return <Navigate to="/unauthorized" replace />;
  return children;
};

// ── Role-based redirect ──────────────────────────────────────────────────
const RedirectByRole = () => {
  const { user, loading, role } = useAuth();
  if (loading) return <PageLoader />;
  if (!user)   return <Navigate to="/login" replace />;
  if (role === 'super_admin')  return <Navigate to="/admin/dashboard"   replace />;
  if (role === 'school_admin') return <Navigate to="/school/dashboard"  replace />;
  if (role === 'teacher')      return <Navigate to="/teacher/dashboard" replace />;
  return <Navigate to="/login" replace />;
};

const P = ({ roles, children }) => <RequireAuth roles={roles}>{children}</RequireAuth>;

const UnauthorizedPage = () => (
  <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh',
    flexDirection:'column', gap:16, fontFamily:'Sora,sans-serif', background:'#F8FAFC' }}>
    <div style={{ fontSize:72 }}>🚫</div>
    <h1 style={{ fontSize:24, fontWeight:800, color:'#0A1628', margin:0 }}>Access Denied</h1>
    <p style={{ color:'#64748B', margin:0 }}>You don't have permission to view this page.</p>
    <a href="/" style={{ color:'#1D9E75', fontWeight:700, textDecoration:'none' }}>← Go to Dashboard</a>
  </div>
);

const NotFoundPage = () => (
  <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh',
    flexDirection:'column', gap:16, fontFamily:'Sora,sans-serif', background:'#F8FAFC' }}>
    <div style={{ fontSize:72 }}>🔍</div>
    <h1 style={{ fontSize:24, fontWeight:800, color:'#0A1628', margin:0 }}>Page Not Found</h1>
    <p style={{ color:'#64748B', margin:0 }}>The page you're looking for doesn't exist.</p>
    <a href="/" style={{ color:'#1D9E75', fontWeight:700, textDecoration:'none' }}>← Go to Dashboard</a>
  </div>
);

const AppRoutes = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Public */}
      <Route path="/login"        element={<Login />} />
      <Route path="/"             element={<RedirectByRole />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />

      {/* ── SUPER ADMIN ── */}
      <Route path="/admin"               element={<P roles={['super_admin']}><ErrorBoundary><SADashboard /></ErrorBoundary></P>} />
      <Route path="/admin/dashboard"     element={<P roles={['super_admin']}><ErrorBoundary><SADashboard /></ErrorBoundary></P>} />
      <Route path="/admin/students"      element={<P roles={['super_admin']}><ErrorBoundary><SAStudents /></ErrorBoundary></P>} />
      <Route path="/admin/schools"       element={<P roles={['super_admin']}><ErrorBoundary><SASchools /></ErrorBoundary></P>} />
      <Route path="/admin/doubts"        element={<P roles={['super_admin']}><ErrorBoundary><SADoubts /></ErrorBoundary></P>} />
      <Route path="/admin/content"       element={<P roles={['super_admin']}><ErrorBoundary><SAContent /></ErrorBoundary></P>} />
      <Route path="/admin/subscriptions" element={<P roles={['super_admin']}><ErrorBoundary><SASubscriptions /></ErrorBoundary></P>} />
      <Route path="/admin/plans"         element={<P roles={['super_admin']}><ErrorBoundary><SAPlans /></ErrorBoundary></P>} />
      <Route path="/admin/reports"       element={<P roles={['super_admin']}><ErrorBoundary><SAReports /></ErrorBoundary></P>} />
      <Route path="/admin/settings"      element={<P roles={['super_admin']}><ErrorBoundary><SASettings /></ErrorBoundary></P>} />
      <Route path="/admin/career"       element={<P roles={['super_admin']}><ErrorBoundary><SACareer /></ErrorBoundary></P>} />
      <Route path="/admin/phase4"       element={<P roles={['super_admin']}><ErrorBoundary><SAPhase4 /></ErrorBoundary></P>} />

      {/* ── SCHOOL ADMIN ── */}
      <Route path="/school/dashboard"   element={<P roles={['school_admin']}><ErrorBoundary><SchoolDashboard /></ErrorBoundary></P>} />
      <Route path="/school/students"    element={<P roles={['school_admin']}><ErrorBoundary><SchoolStudents /></ErrorBoundary></P>} />
      <Route path="/school/teachers"    element={<P roles={['school_admin']}><ErrorBoundary><SchoolTeachers /></ErrorBoundary></P>} />
      <Route path="/school/classes"     element={<P roles={['school_admin']}><ErrorBoundary><SchoolClasses /></ErrorBoundary></P>} />
      <Route path="/school/assignments" element={<P roles={['school_admin']}><ErrorBoundary><SchoolAssignments /></ErrorBoundary></P>} />
      <Route path="/school/performance" element={<P roles={['school_admin']}><ErrorBoundary><SchoolPerformance /></ErrorBoundary></P>} />
      <Route path="/school/reports"     element={<P roles={['school_admin']}><ErrorBoundary><SchoolReports /></ErrorBoundary></P>} />
      <Route path="/school/doubts"      element={<P roles={['school_admin']}><ErrorBoundary><SchoolDoubts /></ErrorBoundary></P>} />
      <Route path="/school/license"     element={<P roles={['school_admin']}><ErrorBoundary><SchoolLicense /></ErrorBoundary></P>} />
      <Route path="/school/settings"    element={<P roles={['school_admin']}><ErrorBoundary><SchoolSettings /></ErrorBoundary></P>} />

      {/* ── TEACHER ── */}
      <Route path="/teacher/dashboard"      element={<P roles={['teacher']}><ErrorBoundary><TeacherDashboard /></ErrorBoundary></P>} />
      <Route path="/teacher/students"       element={<P roles={['teacher']}><ErrorBoundary><TeacherStudents /></ErrorBoundary></P>} />
      <Route path="/teacher/doubts"         element={<P roles={['teacher']}><ErrorBoundary><TeacherDoubts /></ErrorBoundary></P>} />
      <Route path="/teacher/live-class"     element={<P roles={['teacher']}><ErrorBoundary><TeacherLiveClass /></ErrorBoundary></P>} />
      <Route path="/teacher/live"           element={<P roles={['teacher']}><ErrorBoundary><TeacherLiveClass /></ErrorBoundary></P>} />
      <Route path="/teacher/schedule"       element={<P roles={['teacher']}><ErrorBoundary><TeacherSchedule /></ErrorBoundary></P>} />
      <Route path="/teacher/recordings"     element={<P roles={['teacher']}><ErrorBoundary><TeacherRecordings /></ErrorBoundary></P>} />
      <Route path="/teacher/assignments"    element={<P roles={['teacher']}><ErrorBoundary><TeacherAssignments /></ErrorBoundary></P>} />
      <Route path="/teacher/answer-checker" element={<P roles={['teacher']}><ErrorBoundary><TeacherAnswerChecker /></ErrorBoundary></P>} />
      <Route path="/teacher/performance"    element={<P roles={['teacher']}><ErrorBoundary><TeacherPerformance /></ErrorBoundary></P>} />
      <Route path="/teacher/reports"        element={<P roles={['teacher']}><ErrorBoundary><TeacherReports /></ErrorBoundary></P>} />

      {/* 404 — FIXED: no longer silently redirects */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  </Suspense>
);

export default AppRoutes;
